import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/api';
import { useAuth } from './AuthContext';

const WorkspaceContext = createContext(null);
const CACHE_TTL = 60_000; // 1 minute

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, rawSetCurrentWorkspace] = useState(null);
  const [boards, setBoards] = useState([]);
  const [sharedBoards, setSharedBoards] = useState([]);
  const [loading, setLoading] = useState(false);

  // Track which workspace's boards we last fetched to prevent the double-fetch
  // that happens when fetchWorkspaces sets currentWorkspace AND the useEffect
  // watching currentWorkspace both call fetchBoards for the same workspace.
  const lastFetchedWorkspaceId = useRef(null);

  const setCurrentWorkspace = useCallback((workspace) => {
    if (workspace) {
      localStorage.setItem('last_workspace_id', workspace.id);
    } else {
      localStorage.removeItem('last_workspace_id');
    }
    rawSetCurrentWorkspace(workspace);
  }, []);

  // Default-workspace rule: with no remembered choice, land the user in THEIR
  // OWN workspace — first the one named after them ("Priya's Workspace"),
  // else any workspace they own, else the first visible one.
  const resolveDefaultWorkspace = useCallback((list, u) => {
    if (!Array.isArray(list) || !list.length) return null;
    const lastId = localStorage.getItem('last_workspace_id');
    const remembered = list.find(w => w.id === lastId);
    if (remembered) return remembered;
    const firstName = ((u?.name || '').trim().split(/\s+/)[0] || '').toLowerCase();
    const ownNamed = firstName && list.find(w => (w.name || '').trim().toLowerCase().startsWith(firstName));
    return ownNamed || list.find(w => w.owner_id === u?.id) || list[0];
  }, []);

  // opts.silent: refresh the list without touching `loading` — for background
  // refreshes (workspace header counts, tab focus) that must not flash the UI.
  const fetchBoards = useCallback(async (workspaceId, opts = {}) => {
    if (!workspaceId) return;
    const silent = !!opts.silent;
    try {
      if (!silent) setLoading(true);

      // ── Stale-while-revalidate: show cached boards instantly ──
      const cacheKey = `boards_cache_${workspaceId}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) {
          if (!silent) setBoards(data);
          if (!silent) setLoading(false);
          // Still revalidate silently in the background
        } else if (!silent) {
          setBoards(data); // show stale immediately
        }
      }

      // slim=1: listing doesn't need each board's full column schema (much smaller/faster)
      const response = await api.get(`/boards/workspace/${workspaceId}?slim=1`);
      setBoards(response.data);
      localStorage.setItem(cacheKey, JSON.stringify({ data: response.data, ts: Date.now() }));
    } catch (error) {
      console.error('Error fetching boards:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const fetchWorkspaces = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);

      // ── Stale-while-revalidate: show cached workspaces instantly ──
      const cached = localStorage.getItem('workspaces_cache');
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (data?.length) {
          setWorkspaces(data);
          // Also restore boards from cache while fresh data loads
          const resolved = resolveDefaultWorkspace(data, user);
          if (resolved) {
            rawSetCurrentWorkspace(resolved);
            lastFetchedWorkspaceId.current = resolved.id;
            // Show cached boards immediately
            const boardCache = localStorage.getItem(`boards_cache_${resolved.id}`);
            if (boardCache) {
              const { data: cachedBoards } = JSON.parse(boardCache);
              setBoards(cachedBoards);
            }
          }
        }
      }

      const response = await api.get('/workspaces');
      setWorkspaces(response.data);
      localStorage.setItem('workspaces_cache', JSON.stringify({ data: response.data, ts: Date.now() }));

      if (response.data.length > 0) {
        const resolved = resolveDefaultWorkspace(response.data, user);
        rawSetCurrentWorkspace(resolved);
        // Fetch boards directly — mark as fetched so the useEffect below
        // doesn't fire a second identical request.
        lastFetchedWorkspaceId.current = resolved.id;
        fetchBoards(resolved.id);
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setLoading(false);
    }
  }, [user, fetchBoards, resolveDefaultWorkspace]);

  const fetchSharedBoards = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get('/boards/shared/me');
      setSharedBoards(response.data);
    } catch (error) {
      console.error('Error fetching shared boards:', error);
    }
  }, [user]);

  const createWorkspace = async (name, description) => {
    try {
      const response = await api.post('/workspaces', { name, description });
      const updated = [...workspaces, response.data];
      setWorkspaces(updated);
      localStorage.setItem('workspaces_cache', JSON.stringify({ data: updated, ts: Date.now() }));
      return response.data;
    } catch (error) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  };

  const createBoard = async (workspaceId, name, description) => {
    try {
      const response = await api.post('/boards', { workspace_id: workspaceId, name, description });
      const updated = [...boards, response.data];
      setBoards(updated);
      localStorage.setItem(`boards_cache_${workspaceId}`, JSON.stringify({ data: updated, ts: Date.now() }));
      return response.data;
    } catch (error) {
      console.error('Error creating board:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (user) {
      fetchWorkspaces();
      fetchSharedBoards();
    }
  }, [user, fetchWorkspaces, fetchSharedBoards]);

  // Only fetch boards when the workspace changes AND it wasn't already fetched
  // by fetchWorkspaces in the same cycle (prevents the double-fetch).
  useEffect(() => {
    if (!currentWorkspace) return;
    if (lastFetchedWorkspaceId.current === currentWorkspace.id) return;
    lastFetchedWorkspaceId.current = currentWorkspace.id;
    fetchBoards(currentWorkspace.id);
  }, [currentWorkspace, fetchBoards]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        currentWorkspace,
        setCurrentWorkspace,
        boards,
        sharedBoards,
        loading,
        fetchWorkspaces,
        fetchBoards,
        fetchSharedBoards,
        createWorkspace,
        createBoard,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
